<template>
  <div class="settings">
    <h1>系统设置</h1>

    <!-- 运营配置：工作模式（2026-08-31 功能合并） -->
    <el-card style="margin-bottom: 20px">
      <template #header>
        <span>🛠 运营配置 · 工作模式</span>
      </template>
      <el-form label-width="140px">
        <el-form-item label="运行模式">
          <el-radio-group v-model="workMode.work_mode">
            <el-radio-button value="auto">🤖 全自动</el-radio-button>
            <el-radio-button value="semi">🧭 半自动</el-radio-button>
            <el-radio-button value="manual">✋ 手动</el-radio-button>
          </el-radio-group>
          <div class="tip">
            全自动=截流+互动+跟进全开；半自动=只自动采集，不评论/关注/私信；手动=全部人工触发
          </div>
        </el-form-item>
        <el-form-item label="风控等级">
          <el-radio-group v-model="workMode.risk_level">
            <el-radio-button value="conservative">🛡 保守</el-radio-button>
            <el-radio-button value="standard">⚖️ 标准</el-radio-button>
            <el-radio-button value="aggressive">🚀 激进</el-radio-button>
          </el-radio-group>
          <div class="tip">决定每账号每小时评论/关注/私信限额（保守减半，激进加倍）。新号建议保守</div>
        </el-form-item>
        <el-form-item label="一键暂停">
          <el-switch v-model="workMode.auto_paused" active-text="已暂停" inactive-text="运行中" />
          <div class="tip">开启后立即阻止所有自动任务（巡猎/批量跟进/定时任务），适合需要紧急收手时</div>
        </el-form-item>
        <el-form-item>
          <el-button type="primary" @click="saveWorkMode" :loading="savingMode">保存工作模式</el-button>
          <span v-if="modeSaved" style="margin-left:12px;color:#67c23a">✅ 已保存</span>
        </el-form-item>
      </el-form>
    </el-card>
    
    <el-row :gutter="20">
      <el-col :xs="24" :sm="12">
        <el-card>
          <template #header>
            <span>自动采集设置</span>
          </template>
          <el-form label-width="120px">
            <el-form-item label="自动采集">
              <el-switch v-model="autoCollect.enabled" active-text="开启" inactive-text="关闭" />
              <div class="tip">开启后将在下方时间段内自动启动采集（不会重复触发，每天每时段一次）</div>
            </el-form-item>
            <el-form-item label="采集时间段">
              <div v-for="(w, i) in autoCollect.windows" :key="i" class="window-row">
                <el-time-select
                  v-model="w[0]"
                  placeholder="开始时间"
                  start="00:00"
                  end="23:30"
                  step="00:30"
                  style="width: 120px"
                />
                <span style="margin: 0 8px">至</span>
                <el-time-select
                  v-model="w[1]"
                  placeholder="结束时间"
                  start="00:00"
                  end="23:30"
                  step="00:30"
                  style="width: 120px"
                />
                <el-button type="danger" size="small" circle @click="removeWindow(i)">✕</el-button>
              </div>
              <el-button size="small" type="primary" plain @click="addWindow">+ 添加时间段</el-button>
            </el-form-item>
            <el-form-item label="最近触发">
              <span v-if="autoCollect.last_run">{{ autoCollect.last_run }}</span>
              <span v-else style="color: #999">尚未触发</span>
            </el-form-item>
            <el-form-item>
              <el-button type="primary" @click="saveAutoCollect" :loading="savingAuto">保存自动采集设置</el-button>
            </el-form-item>
          </el-form>
        </el-card>
      </el-col>
      
      <el-col :xs="24" :sm="12">
        <el-card>
          <template #header>
            <span>基础设置</span>
          </template>
          <el-form :model="settings" label-width="120px">
            <el-form-item label="系统名称">
              <el-input v-model="settings.app_name" placeholder="找客户·AI客源引擎" />
            </el-form-item>
            <el-form-item label="最大并发任务">
              <el-input-number v-model="settings.max_concurrent" :min="1" :max="50" />
            </el-form-item>
            <el-form-item label="请求延迟(秒)">
              <el-slider v-model="settings.delay_range" range :min="1" :max="20" />
              <span>{{ settings.delay_range[0] }}s - {{ settings.delay_range[1] }}s</span>
            </el-form-item>
            <el-form-item>
              <el-button type="primary" @click="saveSettings">保存设置</el-button>
            </el-form-item>
          </el-form>
        </el-card>
      </el-col>
      
      <el-col :xs="24" :sm="12">
        <el-card>
          <template #header>
            <span>代理设置</span>
          </template>
          <el-form :model="proxySettings" label-width="120px">
            <el-form-item label="启用代理">
              <el-switch v-model="proxySettings.use_proxy" active-text="开" inactive-text="关" />
              <div class="tip">多账号同时运行时建议为每个账号配置独立IP，降低风控关联</div>
            </el-form-item>
            <el-form-item label="代理类型">
              <el-select v-model="proxySettings.type" style="width: 100%">
                <el-option label="HTTP代理" value="http" />
                <el-option label="SOCKS5代理" value="socks5" />
              </el-select>
            </el-form-item>
            <el-form-item label="代理地址">
              <el-input v-model="proxySettings.host" placeholder="127.0.0.1" />
            </el-form-item>
            <el-form-item label="代理端口">
              <el-input-number v-model="proxySettings.port" :min="1" :max="65535" />
            </el-form-item>
            <el-form-item label="用户名(可选)">
              <el-input v-model="proxySettings.username" placeholder="代理认证用户名，无则留空" />
            </el-form-item>
            <el-form-item label="密码(可选)">
              <el-input v-model="proxySettings.password" type="password" placeholder="代理认证密码，无则留空" show-password />
            </el-form-item>
            <el-form-item>
              <el-button type="primary" @click="testProxy" :loading="proxyTesting">测试连接</el-button>
              <el-button type="success" @click="saveProxySettings" style="margin-left:8px">保存代理设置</el-button>
              <span v-if="proxySaved" style="margin-left:12px;color:#67c23a">✅ 已保存</span>
            </el-form-item>
          </el-form>
        </el-card>
      </el-col>
    </el-row>
  </div>
</template>

<script>
import api from '../api'

export default {
  name: 'Settings',
  data() {
    return {
      settings: {
        app_name: '找客户·AI客源引擎',
        max_concurrent: 16,
        delay_range: [2, 8]
      },
      proxySettings: {
        use_proxy: false,
        type: 'http',
        host: '',
        port: 8080,
        username: '',
        password: ''
      },
      proxyTesting: false,
      proxySaved: false,
      autoCollect: {
        enabled: false,
        windows: [['09:00', '12:00'], ['18:30', '21:30']],
        last_run: ''
      },
      savingAuto: false,
      workMode: {
        work_mode: 'auto',
        risk_level: 'standard',
        auto_paused: false
      },
      savingMode: false,
      modeSaved: false
    }
  },
  async mounted() {
    // 加载后端配置（系统名称等）
    try {
      const r = await api.get('/leads/settings')
      if (r && r.app_name) {
        this.settings.app_name = r.app_name
      }
    } catch (e) {
      console.error('加载设置失败', e)
    }
    // 加载自动采集配置
    this.loadAutoCollect()
    // 加载运营工作模式
    this.loadWorkMode()
    // 加载代理配置
    this.loadProxySettings()
  },
  methods: {
    async loadWorkMode() {
      try {
        const r = await api.get('/settings/work-mode')
        if (r) {
          this.workMode.work_mode = r.work_mode || 'auto'
          this.workMode.risk_level = r.risk_level || 'standard'
          this.workMode.auto_paused = !!r.auto_paused
        }
      } catch (e) {
        console.error('加载工作模式失败', e)
      }
    },
    async saveWorkMode() {
      this.savingMode = true
      this.modeSaved = false
      try {
        const r = await api.put('/settings/work-mode', {
          work_mode: this.workMode.work_mode,
          risk_level: this.workMode.risk_level,
          auto_paused: this.workMode.auto_paused
        })
        if (r.ok) {
          this.$message.success('工作模式已保存')
          this.modeSaved = true
          setTimeout(() => { this.modeSaved = false }, 3000)
        } else {
          this.$message.error(r.detail || '保存失败')
        }
      } catch (e) {
        this.$message.error('保存失败：' + (e.response?.data?.detail || e.message))
      } finally {
        this.savingMode = false
      }
    },
    async loadAutoCollect() {
      try {
        const r = await api.get('/settings/auto-collect')
        this.autoCollect.enabled = r.enabled
        this.autoCollect.windows = (r.windows && r.windows.length) ? r.windows : [['09:00', '12:00'], ['18:30', '21:30']]
        this.autoCollect.last_run = r.last_run || ''
      } catch (e) {
        console.error('加载自动采集配置失败', e)
      }
    },
    addWindow() {
      this.autoCollect.windows.push(['09:00', '12:00'])
    },
    removeWindow(i) {
      this.autoCollect.windows.splice(i, 1)
    },
    async saveAutoCollect() {
      if (!this.autoCollect.windows.length) {
        this.$message.warning('至少保留一个采集时间段')
        return
      }
      this.savingAuto = true
      try {
        const r = await api.put('/settings/auto-collect', {
          enabled: this.autoCollect.enabled,
          windows: this.autoCollect.windows
        })
        if (r.ok) {
          this.$message.success('自动采集设置已保存')
        } else {
          this.$message.error(r.detail || '保存失败')
        }
      } catch (e) {
        this.$message.error('保存失败：' + (e.response?.data?.detail || e.message))
      } finally {
        this.savingAuto = false
      }
    },
    async saveSettings() {
      try {
        await api.post('/leads/settings', {
          app_name: this.settings.app_name
        })
        this.$message.success('设置已保存')
      } catch (e) {
        this.$message.error('保存失败：' + (e.response?.data?.detail || e.message))
      }
    },
    async loadProxySettings() {
      try {
        const r = await api.get('/leads/windcontrol')
        const cfg = r || {}
        this.proxySettings.use_proxy = !!cfg.use_proxy
        const def = (cfg.proxies && (cfg.proxies.default || cfg.proxies['0'])) || ''
        if (def) {
          // http://user:pass@host:port 解析回填
          let rest = def
          let user = '', pass = ''
          const proto = rest.includes('://') ? rest.split('://')[0] : 'http'
          rest = rest.includes('://') ? rest.split('://')[1] : rest
          if (rest.includes('@')) {
            const cred = rest.split('@')[0]
            rest = rest.split('@')[1]
            const cp = cred.split(':')
            user = cp[0] || ''
            pass = cp.slice(1).join(':') || ''
          }
          const hp = rest.split(':')
          this.proxySettings.type = proto
          this.proxySettings.host = hp[0] || ''
          this.proxySettings.port = parseInt(hp[1] || '8080', 10) || 8080
          this.proxySettings.username = user
          this.proxySettings.password = pass
        }
      } catch (e) {
        console.error('加载代理配置失败', e)
      }
    },
    buildProxyUrl() {
      if (!this.proxySettings.host) return ''
      const auth = this.proxySettings.username
        ? `${encodeURIComponent(this.proxySettings.username)}:${encodeURIComponent(this.proxySettings.password || '')}@`
        : ''
      return `${this.proxySettings.type}://${auth}${this.proxySettings.host}:${this.proxySettings.port}`
    },
    async saveProxySettings() {
      try {
        if (this.proxySettings.use_proxy && !this.proxySettings.host) {
          this.$message.warning('请填写代理地址')
          return
        }
        const cfg = {
          use_proxy: this.proxySettings.use_proxy,
          proxies: this.proxySettings.use_proxy ? { default: this.buildProxyUrl() } : {}
        }
        await api.post('/leads/windcontrol', cfg)
        this.$message.success('代理设置已保存')
        this.proxySaved = true
        setTimeout(() => { this.proxySaved = false }, 3000)
      } catch (e) {
        this.$message.error('保存失败：' + (e.response?.data?.detail || e.message))
      }
    },
    async testProxy() {
      if (!this.proxySettings.host) {
        this.$message.warning('请先填写代理地址')
        return
      }
      this.proxyTesting = true
      try {
        // 用后端代理设置探测目标站连通性（避免浏览器跨域）
        const r = await api.post('/leads/windcontrol', {
          use_proxy: this.proxySettings.use_proxy,
          proxies: this.proxySettings.use_proxy ? { default: this.buildProxyUrl() } : {}
        })
        this.$message.success('代理已保存，可在任务日志确认连通性')
      } catch (e) {
        this.$message.error('测试失败：' + (e.response?.data?.detail || e.message))
      } finally {
        this.proxyTesting = false
      }
    }
  }
}
</script>

<style scoped>
.settings {
  padding: 20px;
}
.window-row {
  display: flex;
  align-items: center;
  margin-bottom: 8px;
}
.tip {
  font-size: 12px;
  color: var(--el-text-color-secondary, #999);
  margin-top: 4px;
}
/* 手机端：文字左靠 + 表单竖向排布，输入区最大化 */
@media (max-width: 767px) {
  .settings :deep(.el-form-item) { display: block; }
  .settings :deep(.el-form-item__label) {
    width: 100% !important; text-align: left; float: none;
    line-height: 1.4; margin-bottom: 4px; font-size: 15px;
  }
  .settings :deep(.el-form-item__content) { margin-left: 0 !important; }
  .window-row { flex-wrap: wrap; gap: 6px; }
  .window-row .el-time-select { width: 100% !important; }
}
</style>
